import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  InputErrorMessage,
  Label,
  Modal,
  MultiEmailInput,
  Notification,
} from 'shared/components';
import {
  getApiErrorMessage,
  getUnsubmittedEmailError,
  normalizeEmail,
  toChunks,
  uniqueEmails,
} from 'shared/helpers';
import { unassignOrderUsers } from 'orders/OrderContainer/actions';
import './styles.scss';

// unassign_users accepts at most 50 emails and 10 orders per request
const EMAILS_PER_REQUEST = 50;
const ORDERS_PER_REQUEST = 10;
const PREVIEWED_ORDERS = 5;

const STATUS_ORDER = ['removed', 'not_assigned', 'unknown_email'];

// Each request covers only some of the orders, so an email counts as removed
// when any request removed it, and as not assigned only when none did
const mergeResults = (merged, rows) =>
  rows.reduce((acc, row) => {
    const key = normalizeEmail(row.email);
    const current = acc[key];
    if (!current) {
      return { ...acc, [key]: row };
    }
    if (row.status !== 'removed') {
      return acc;
    }
    return {
      ...acc,
      [key]: {
        ...current,
        status: 'removed',
        licenses_count:
          (current.status === 'removed' ? current.licenses_count : 0) +
          row.licenses_count,
      },
    };
  }, merged);

const RemoveOrderUsersForm = ({ orders, closeCb, confirmCb }) => {
  const { t } = useTranslation();
  const [dirty, setDirty] = useState(false);
  const [isDirtyFormAlertDisplayed, setDirtyFormAlertDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userEmails, setUserEmails] = useState({});
  const [formError, setFormError] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  // The list clears its selection after saving, the result view still needs the orders
  const [selectedOrders] = useState(orders);

  const isSingleOrder = selectedOrders.length === 1;
  const orderLabels = selectedOrders.map((order) => order.store_id || order.id);
  const hiddenOrdersCount = selectedOrders.length - PREVIEWED_ORDERS;
  const emailCount = get(userEmails, 'emails.length', 0);

  const statusLabels = {
    removed: t('Removed'),
    not_assigned: t('Not assigned'),
    unknown_email: t('Unknown email'),
  };

  const getError = (err, isFirstRequest) => {
    if (get(err, 'response.status') === 404) {
      return isFirstRequest
        ? t(
            'You can not remove users from one or more of the selected orders, so nothing was removed.',
          )
        : t('One or more of the selected orders can not be used.');
    }
    return (
      getApiErrorMessage(err) ||
      t('There was an error while saving your changes')
    );
  };

  const removeUsers = async (emails) => {
    const orderGroups = toChunks(selectedOrders, ORDERS_PER_REQUEST);
    const emailBatches = toChunks(emails, EMAILS_PER_REQUEST);
    const total = orderGroups.length * emailBatches.length;
    let merged = {};
    let done = 0;
    for (const [groupIndex, orderGroup] of orderGroups.entries()) {
      for (const emailBatch of emailBatches) {
        setProgress({ done, total });
        try {
          const res = await unassignOrderUsers({
            emails: emailBatch,
            order_ids: orderGroup.map((order) => order.id),
          });
          merged = mergeResults(merged, get(res, 'data', []));
        } catch (err) {
          // Requests before this one are saved, so their results are still reported
          return {
            rows: Object.values(merged),
            error: getError(err, done === 0),
            notProcessedOrders: orderGroups.slice(groupIndex).flat(),
          };
        }
        done += 1;
      }
    }
    return { rows: Object.values(merged), error: '', notProcessedOrders: [] };
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (results) {
      return closeCb();
    }

    if (userEmails.value) {
      setUserEmails({
        ...userEmails,
        validationError: getUnsubmittedEmailError(userEmails.value),
      });
      return false;
    }

    const emails = uniqueEmails(get(userEmails, 'emails', []));
    if (!emails.length) {
      setFormError(t('Enter at least one email'));
      return false;
    }

    setFormError('');
    setLoading(true);
    const outcome = await removeUsers(emails);
    setProgress(null);
    setLoading(false);

    if (!outcome.rows.length) {
      setFormError(outcome.error);
      return false;
    }

    // Orders that were not fully processed stay selected, so they can be retried
    confirmCb(outcome.notProcessedOrders);

    const isEverythingRemoved =
      !outcome.error && outcome.rows.every((row) => row.status === 'removed');
    if (isEverythingRemoved) {
      Notification(
        'success',
        t('Changes saved successfully'),
        t('License users have been removed'),
      );
      return closeCb();
    }

    setResults(outcome);
    return true;
  };

  const handleClose = () => {
    if (loading) {
      return false;
    }
    if (!dirty || results) {
      return closeCb();
    }
    return setDirtyFormAlertDisplay(true);
  };

  const renderSummary = () => (
    <div className="RemoveOrderUsersForm-summary">
      {STATUS_ORDER.map((status) => {
        const statusCount = results.rows.filter(
          (row) => row.status === status,
        ).length;
        return (
          statusCount > 0 && (
            <span key={status} className={`summary-item is-${status}`}>
              {`${statusLabels[status]}: ${statusCount}`}
            </span>
          )
        );
      })}
    </div>
  );

  const renderResults = () => {
    const rows = STATUS_ORDER.flatMap((status) =>
      results.rows.filter((row) => row.status === status),
    );
    const notProcessedLabels = results.notProcessedOrders.map(
      (order) => order.store_id || order.id,
    );
    const hiddenNotProcessedCount =
      notProcessedLabels.length - PREVIEWED_ORDERS;
    return (
      <div className="RemoveOrderUsersForm-results">
        {results.error && (
          <div className="RemoveOrderUsersForm-warning">
            <p>{results.error}</p>
            <p>
              {`${t('These orders were not fully processed')}: ${notProcessedLabels.slice(0, PREVIEWED_ORDERS).join(', ')}`}
              {hiddenNotProcessedCount > 0 && ` +${hiddenNotProcessedCount}`}
            </p>
            {!isSingleOrder && (
              <p>
                {t(
                  'They are still selected in the list, so you can try again.',
                )}
              </p>
            )}
          </div>
        )}
        {renderSummary()}
        <div className="RemoveOrderUsersForm-table">
          <table>
            <thead>
              <tr>
                <th>{t('Email')}</th>
                <th>{t('Result')}</th>
                <th>{t('Licenses removed')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.email} className={`is-${row.status}`}>
                  <td>{row.email}</td>
                  <td className="result-status">
                    {statusLabels[row.status] || row.status}
                  </td>
                  <td>{row.status === 'removed' ? row.licenses_count : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="RemoveOrderUsersForm-help">
          {isSingleOrder
            ? t(
                'Not assigned: the user has no user-based license in this order. Unknown email: there is no license user with this email.',
              )
            : t(
                'Not assigned: the user has no user-based license in these orders. Unknown email: there is no license user with this email.',
              )}
        </p>
      </div>
    );
  };

  return (
    <Modal
      title={
        isSingleOrder
          ? t('Remove users from this order')
          : t('Remove users from orders')
      }
      closeCb={handleClose}
      confirmCb={handleSubmit}
      confirmText={results ? t('Close') : t('Remove users')}
      confirmTheme={results ? 'success' : 'error'}
      disabled={loading}
      size="lg"
    >
      <div className="RemoveOrderUsersForm">
        <div className="RemoveOrderUsersForm-orders">
          <strong>
            {isSingleOrder
              ? `${t('Order')}: ${orderLabels[0]}`
              : `${t('Selected orders')}: ${selectedOrders.length}`}
          </strong>
          {!isSingleOrder && (
            <span>
              {orderLabels.slice(0, PREVIEWED_ORDERS).join(', ')}
              {hiddenOrdersCount > 0 && ` +${hiddenOrdersCount}`}
            </span>
          )}
        </div>
        {results ? (
          renderResults()
        ) : (
          <div>
            <Label text={t('User emails')} inputId="remove-order-users" />
            <p className="RemoveOrderUsersForm-help">
              {isSingleOrder
                ? t(
                    'These users are removed from every user-based license in this order. Key-based licenses are not affected.',
                  )
                : t(
                    'These users are removed from every user-based license in the selected orders. Key-based licenses are not affected.',
                  )}
            </p>
            <MultiEmailInput
              emails={get(userEmails, 'emails')}
              onEmailSubmit={(val) => {
                setDirty(true);
                setFormError('');
                setUserEmails(val);
              }}
              isDisabled={loading}
            />
            <InputErrorMessage text={get(userEmails, 'validationError', '')} />
            {emailCount > EMAILS_PER_REQUEST && (
              <p className="RemoveOrderUsersForm-help">
                {`${t('Emails entered')}: ${emailCount}. ${t('They are sent in batches of 50.')}`}
              </p>
            )}
            {selectedOrders.length > ORDERS_PER_REQUEST && (
              <p className="RemoveOrderUsersForm-help">
                {t('The selected orders are sent in groups of 10.')}
              </p>
            )}
            {progress && progress.total > 1 && (
              <div className="RemoveOrderUsersForm-progress">
                {`${t('Processing')} ${progress.done + 1} / ${progress.total}`}
              </div>
            )}
            <InputErrorMessage text={formError} />
          </div>
        )}
      </div>
      {isDirtyFormAlertDisplayed && (
        <DirtyFormAlert
          dirty={dirty}
          closeAlert={() => setDirtyFormAlertDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

RemoveOrderUsersForm.propTypes = {
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      store_id: PropTypes.string,
    }),
  ).isRequired,
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
};

export default RemoveOrderUsersForm;
